'use client';
import React from 'react';
/**
 * GlobalBookingModal - Global singleton instance of the booking modal.
 *
 * This module exports a component that renders a single, global instance of the
 * BookingModal component. It connects to the BookingModalContext to receive state
 * and can be controlled from any component using the useBookingModal hook.
 *
 * This component should be rendered once at the root layout level, typically
 * alongside the BookingModalProvider.
 *
 * @module
 */

import { useBookingModal } from './BookingModalContext';
import { BookingModal } from './BookingModal';

/**
 * GlobalBookingModal - Renders a globally controlled booking modal instance.
 *
 * This component subscribes to the BookingModalContext and renders the BookingModal
 * with the current modal state. It serves as the view layer for the modal logic
 * that is managed by BookingModalProvider.
 *
 * @component
 * @returns {React.ReactElement} The BookingModal component with state and handlers
 *
 * @example
 * ```tsx
 * // In root layout
 * export default function RootLayout({ children }: { children: ReactNode }) {
 *   return (
 *     <html>
 *       <body>
 *         <BookingModalProvider>
 *           {children}
 *           <GlobalBookingModal />
 *         </BookingModalProvider>
 *       </body>
 *     </html>
 *   );
 * }
 *
 * // Anywhere in the app
 * function HeroSection() {
 *   const { openModal } = useBookingModal();
 *
 *   return (
 *     <button onClick={() => openModal({ placement: 'hero' })}>
 *       Talk to us
 *     </button>
 *   );
 * }
 * ```
 *
 * @implementation
 * - Subscribes to BookingModalContext for modal state (isOpen) and close handler
 * - Passes state directly to BookingModal component
 * - No local state management; all state is managed by context
 */
export function GlobalBookingModal(): React.ReactElement {
  const { isOpen, closeModal, entry, request, requestKey } = useBookingModal();

  return <BookingModal key={requestKey} isOpen={isOpen} onClose={closeModal} entry={entry} request={request} />;
}
